import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    IconButton,
    Grid,
    TextField,
    TablePagination
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { makeApiRequest } from '../../../utils/api';
import { toast, ToastContainer } from 'react-toastify';

export default function PendingRequests() {
    const token = localStorage.getItem('authToken');
    const [loading, setLoading] = useState(false);
    const [requests, setRequests] = useState([]);
    const [error, setError] = useState(null);
    
    // Details Modal State
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [remarks, setRemarks] = useState('');
    const [moduleNameMap, setModuleNameMap] = useState({});

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        fetchPendingRequests(0, rowsPerPage);
        fetchModuleNames();
    }, []);

    const fetchModuleNames = async () => {
        try {
            const response = await makeApiRequest('post', '/api/module/list', { token });
            const modules = response?.status === 'success' ? (response.data?.data || []) : [];
            const map = {};
            modules.forEach(m => { map[m.module_id] = m.module; });
            setModuleNameMap(map);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchPendingRequests = async (currentPage = page, currentRowsPerPage = rowsPerPage) => {
        setLoading(true);
        setError(null);
        try {
            const response = await makeApiRequest('post', '/api/edit-requests/pending', {
                token,
                page: currentPage + 1,
                per_page: currentRowsPerPage,
            });
            if (response && response.status === 'success') {
                if (response.data && Array.isArray(response.data.data)) {
                    setRequests(response.data.data);
                    setTotalCount(response.data.total || 0);
                } else if (Array.isArray(response.data)) {
                    setRequests(response.data);
                    setTotalCount(response.data.length);
                }
            } else {
                setError('Failed to fetch pending requests.');
            }
        } catch (err) {
            console.error(err);
            setError('Error loading pending requests.');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        fetchPendingRequests(newPage, rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        fetchPendingRequests(0, newRowsPerPage);
    };

    const handleOpenDetails = (request) => {
        setSelectedRequest(request);
        setRemarks('');
        setOpenModal(true);
    };

    const handleCloseDetails = () => {
        setOpenModal(false);
        setSelectedRequest(null);
        setRemarks('');
    };

    const handleApprove = async (id) => {
        setActionLoading(true);
        try {
            const response = await makeApiRequest('post', '/api/edit-requests/approve', {
                token,
                id: Number(id),
                remarks: remarks || undefined
            });
            if (response && response.status === 'success') {
                toast.success('Edit request approved and updates applied!');
                fetchPendingRequests();
                handleCloseDetails();
            } else {
                toast.error(response?.message || 'Failed to approve request.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Error approving request.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async (id) => {
        setActionLoading(true);
        try {
            const response = await makeApiRequest('post', '/api/edit-requests/reject', {
                token,
                id: Number(id),
                remarks: remarks || undefined
            });
            if (response && response.status === 'success') {
                toast.success('Edit request rejected and changes discarded.');
                fetchPendingRequests();
                handleCloseDetails();
            } else {
                toast.error(response?.message || 'Failed to reject request.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Error rejecting request.');
        } finally {
            setActionLoading(false);
        }
    };

    const renderMarksComparison = (request) => {
        if (!request || request.module !== 'marks') return null;

        const oldMap = {};
        request.old_data.forEach(item => {
            oldMap[item.assessment_name] = item.marks;
        });

        return (
            <Table size="small" sx={{ mt: 1 }}>
                <TableHead sx={{ backgroundColor: '#F1F4F9' }}>
                    <TableRow>
                        <TableCell>Assessment Name</TableCell>
                        <TableCell>Field Name</TableCell>
                        <TableCell align="center">Old Mark</TableCell>
                        <TableCell align="center">Proposed Mark</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {request.new_data.map(change => {
                        const oldMarks = oldMap[change.assessment_name] || {};
                        const fields = Object.keys(change.marks);
                        return fields.map((field, idx) => (
                            <TableRow key={`${change.assessment_name}-${field}`}>
                                {idx === 0 && (
                                    <TableCell rowSpan={fields.length} sx={{ fontWeight: 600, verticalAlign: 'top' }}>
                                        {change.assessment_name}
                                    </TableCell>
                                )}
                                <TableCell>{field.replace(/_/g, ' ').toUpperCase()}</TableCell>
                                <TableCell align="center" sx={{ color: 'error.main', fontWeight: 500 }}>
                                    {oldMarks[field] !== undefined && oldMarks[field] !== null ? oldMarks[field] : 'N/A'}
                                </TableCell>
                                <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                                    {change.marks[field] !== undefined && change.marks[field] !== null ? change.marks[field] : 'N/A'}
                                </TableCell>
                            </TableRow>
                        ));
                    })}
                </TableBody>
            </Table>
        );
    };

    const CERTIFICATE_FIELD_LABELS = {
        date: 'Issue Date',
        student_name: 'Student Name',
        course: 'Course Name',
        start_date: 'Start Date',
        end_date: 'End Date',
    };

    const renderCertificateDeleteComparison = (request) => {
        if (!request || request.module !== 'certificate_delete') return null;

        const oldData = request.old_data || {};

        return (
            <Box sx={{ mt: 1 }}>
                <Typography sx={{ color: 'error.main', fontWeight: 600, mb: 1 }}>
                    This certificate will be permanently deleted if approved.
                </Typography>
                <Table size="small">
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Student Name</TableCell>
                            <TableCell>{oldData.student_name || 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>User ID</TableCell>
                            <TableCell>{oldData.user_id || 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell>{oldData.generated_status || 'N/A'}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Box>
        );
    };

    const renderCertificateComparison = (request) => {
        if (!request || (request.module !== 'certificate' && request.module !== 'certificate_status')) return null;

        const oldData = request.old_data || {};
        const newData = request.new_data || {};
        const changedFields = Object.keys(newData).filter(key => key !== 'marks' && key !== 'image');

        return (
            <>
                {newData.image && (
                    <>
                        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Profile Photo</Typography>
                        <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" color="textSecondary">Current</Typography>
                                <Box
                                    component="img"
                                    src={oldData.image ? `${process.env.REACT_APP_URL}/documents/${oldData.image}` : ''}
                                    alt="Current profile"
                                    sx={{ display: 'block', width: 100, height: 100, objectFit: 'cover', borderRadius: 1, border: '2px solid', borderColor: 'error.main' }}
                                />
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" color="textSecondary">Proposed</Typography>
                                <Box
                                    component="img"
                                    src={`${process.env.REACT_APP_URL}/documents/${newData.image}`}
                                    alt="Proposed profile"
                                    sx={{ display: 'block', width: 100, height: 100, objectFit: 'cover', borderRadius: 1, border: '2px solid', borderColor: 'success.main' }}
                                />
                            </Box>
                        </Box>
                    </>
                )}
                {changedFields.length > 0 && (
                    <Table size="small" sx={{ mt: 1 }}>
                        <TableHead sx={{ backgroundColor: '#F1F4F9' }}>
                            <TableRow>
                                <TableCell>Field</TableCell>
                                <TableCell align="center">Old Value</TableCell>
                                <TableCell align="center">New Value</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {changedFields.map(field => (
                                <TableRow key={field}>
                                    <TableCell sx={{ fontWeight: 600 }}>{CERTIFICATE_FIELD_LABELS[field] || field}</TableCell>
                                    <TableCell align="center" sx={{ color: 'error.main', fontWeight: 500 }}>
                                        {oldData[field] || 'N/A'}
                                    </TableCell>
                                    <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                                        {newData[field] || 'N/A'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                {Array.isArray(newData.marks) && newData.marks.length > 0 && (
                    <>
                        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>Marks Comparison</Typography>
                        <Table size="small" sx={{ mt: 1 }}>
                            <TableHead sx={{ backgroundColor: '#F1F4F9' }}>
                                <TableRow>
                                    <TableCell>Assessment Name</TableCell>
                                    <TableCell>Field Name</TableCell>
                                    <TableCell align="center">Old Mark</TableCell>
                                    <TableCell align="center">Proposed Mark</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {newData.marks.map(change => {
                                    const oldEntry = (oldData.marks || []).find(o => o.assessment_id === change.assessment_id) || {};
                                    const oldMarks = oldEntry.marks || {};
                                    const fields = Object.keys(change.marks);
                                    return fields.map((field, idx) => (
                                        <TableRow key={`${change.assessment_name}-${field}`}>
                                            {idx === 0 && (
                                                <TableCell rowSpan={fields.length} sx={{ fontWeight: 600, verticalAlign: 'top' }}>
                                                    {change.assessment_name}
                                                </TableCell>
                                            )}
                                            <TableCell>{field.replace(/_/g, ' ').toUpperCase()}</TableCell>
                                            <TableCell align="center" sx={{ color: 'error.main', fontWeight: 500 }}>
                                                {oldMarks[field] !== undefined && oldMarks[field] !== null ? oldMarks[field] : 'N/A'}
                                            </TableCell>
                                            <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                                                {change.marks[field] !== undefined && change.marks[field] !== null ? change.marks[field] : 'N/A'}
                                            </TableCell>
                                        </TableRow>
                                    ));
                                })}
                            </TableBody>
                        </Table>
                    </>
                )}
            </>
        );
    };

    const renderBatchAssessmentsComparison = (request) => {
        if (!request || request.module !== 'batch_assessments') return null;

        const oldMap = {};
        (request.old_data || []).forEach(item => {
            oldMap[item.generic_name] = item;
        });

        return (
            <Table size="small" sx={{ mt: 1 }}>
                <TableHead sx={{ backgroundColor: '#F1F4F9' }}>
                    <TableRow>
                        <TableCell>Assessment Component</TableCell>
                        <TableCell align="center">Old Config</TableCell>
                        <TableCell align="center">Proposed Config</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(request.new_data || []).map(change => {
                        const oldItem = oldMap[change.generic_name] || {};
                        const oldStatus = oldItem.is_enabled ? `Enabled (${oldItem.specific_name})` : 'Disabled';
                        const newStatus = change.is_enabled ? `Enabled (${change.specific_name})` : 'Disabled';
                        const isChanged = oldItem.is_enabled !== change.is_enabled || oldItem.specific_name !== change.specific_name;

                        return (
                            <TableRow key={change.generic_name} sx={{ backgroundColor: isChanged ? '#fffde7' : 'inherit' }}>
                                <TableCell sx={{ fontWeight: 600 }}>{change.generic_name}</TableCell>
                                <TableCell align="center" sx={{ color: oldItem.is_enabled ? 'success.main' : 'error.main' }}>
                                    {oldStatus}
                                </TableCell>
                                <TableCell align="center" sx={{ color: change.is_enabled ? 'success.main' : 'error.main', fontWeight: isChanged ? 600 : 400 }}>
                                    {newStatus}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        );
    };
    const renderStudentProfileComparison = (request) => {
        if (!request || request.module !== 'student_profile') return null;

        const diffs = [];
        const findDiffs = (oldObj, newObj, prefix = '') => {
            if (!oldObj || !newObj) return;
            Object.keys(newObj).forEach(key => {
                const oldVal = oldObj[key];
                const newVal = newObj[key];
                
                if (typeof newVal === 'object' && newVal !== null) {
                    findDiffs(oldVal || {}, newVal, prefix ? `${prefix} > ${key}` : key);
                } else if (oldVal !== newVal) {
                    if (key.includes('file') || key.includes('copy') || key.includes('path') || key === 'token' || key === 'password') return;
                    diffs.push({
                        field: prefix ? `${prefix} > ${key}` : key,
                        oldVal: oldVal !== undefined && oldVal !== null ? String(oldVal) : 'N/A',
                        newVal: newVal !== undefined && newVal !== null ? String(newVal) : 'N/A'
                    });
                }
            });
        };

        findDiffs(request.old_data, request.new_data);

        return (
            <Table size="small" sx={{ mt: 1 }}>
                <TableHead sx={{ backgroundColor: '#F1F4F9' }}>
                    <TableRow>
                        <TableCell>Field Name</TableCell>
                        <TableCell align="center">Old Value</TableCell>
                        <TableCell align="center">Proposed Value</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {diffs.map(diff => (
                        <TableRow key={diff.field}>
                            <TableCell sx={{ fontWeight: 600 }}>{diff.field.replace(/_/g, ' ').toUpperCase()}</TableCell>
                            <TableCell align="center" sx={{ color: 'error.main', fontWeight: 500 }}>
                                {diff.oldVal}
                            </TableCell>
                            <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                                {diff.newVal}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    };

    const renderCourseComparison = (request) => {
        if (!request || request.module !== 'course') return null;

        const oldData = request.old_data || {};
        const newData = request.new_data || {};

        // Parse module_ids into a numeric id array
        const toIdArray = (val) => {
            if (Array.isArray(val)) return val.map(Number);
            if (typeof val === 'string') return val.split(',').map(s => Number(s.trim())).filter(n => !isNaN(n));
            return [];
        };

        const oldModuleIds = toIdArray(oldData.module_ids);
        const newModuleIds = toIdArray(newData.module_ids);
        const addedModuleIds = newModuleIds.filter(id => !oldModuleIds.includes(id));
        const removedModuleIds = oldModuleIds.filter(id => !newModuleIds.includes(id));
        const getModuleName = (id) => moduleNameMap[id] || `Module ${id}`;

        const scalarFields = [];

        if (oldData.course_name !== newData.course_name) {
            scalarFields.push({ key: 'course_name', label: 'Course Name', oldVal: oldData.course_name, newVal: newData.course_name });
        }

        if (addedModuleIds.length > 0 || removedModuleIds.length > 0) {
            scalarFields.push({
                key: 'module_ids',
                label: 'Modules Changed',
                oldVal: removedModuleIds.length > 0 ? removedModuleIds.map(getModuleName).join(', ') : '—',
                newVal: addedModuleIds.length > 0 ? addedModuleIds.map(getModuleName).join(', ') : '—',
            });
        }

        const oldAssessments = Array.isArray(oldData.assessments) ? oldData.assessments : [];
        const newAssessments = Array.isArray(newData.assessments) ? newData.assessments : [];
        const allGenericNames = Array.from(new Set([
            ...oldAssessments.map(a => a.generic_name),
            ...newAssessments.map(a => a.generic_name),
        ]));

        return (
            <>
                {scalarFields.length > 0 && (
                    <Table size="small" sx={{ mt: 1 }}>
                        <TableHead sx={{ backgroundColor: '#F1F4F9' }}>
                            <TableRow>
                                <TableCell>Field</TableCell>
                                <TableCell align="center">Old Value</TableCell>
                                <TableCell align="center">Proposed Value</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {scalarFields.map(f => (
                                <TableRow key={f.key} sx={{ backgroundColor: f.oldVal !== f.newVal ? '#fffde7' : 'inherit' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>{f.label}</TableCell>
                                    <TableCell align="center" sx={{ color: 'error.main', fontWeight: 500 }}>{f.oldVal ?? 'N/A'}</TableCell>
                                    <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>{f.newVal ?? 'N/A'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}

                {allGenericNames.length > 0 && (
                    <>
                        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>Assessments</Typography>
                        <Table size="small" sx={{ mt: 1 }}>
                            <TableHead sx={{ backgroundColor: '#F1F4F9' }}>
                                <TableRow>
                                    <TableCell>Assessment (Generic Name)</TableCell>
                                    <TableCell align="center">Old Display Name</TableCell>
                                    <TableCell align="center">Proposed Display Name</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allGenericNames.map(generic => {
                                    const oldItem = oldAssessments.find(a => a.generic_name === generic);
                                    const newItem = newAssessments.find(a => a.generic_name === generic);
                                    const oldName = oldItem?.specific_name ?? '—';
                                    const newName = newItem?.specific_name ?? '—';
                                    const changed = oldName !== newName;
                                    return (
                                        <TableRow key={generic} sx={{ backgroundColor: changed ? '#fffde7' : 'inherit' }}>
                                            <TableCell sx={{ fontWeight: 600 }}>{generic}</TableCell>
                                            <TableCell align="center" sx={{ color: 'error.main', fontWeight: 500 }}>{oldName}</TableCell>
                                            <TableCell align="center" sx={{ color: 'success.main', fontWeight: changed ? 600 : 400 }}>{newName}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </>
                )}
            </>
        );
    };

    const renderBatchProfileComparison = (request) => {
        if (!request || request.module !== 'batch_profile') return null;

        const diffs = [];
        const keys = ['batch_name', 'start_date', 'end_date', 'location', 'course', 'status'];
        
        keys.forEach(key => {
            const oldVal = request.old_data[key];
            const newVal = request.new_data[key];
            if (oldVal !== newVal) {
                diffs.push({
                    field: key,
                    oldVal: oldVal !== undefined && oldVal !== null ? String(oldVal) : 'N/A',
                    newVal: newVal !== undefined && newVal !== null ? String(newVal) : 'N/A'
                });
            }
        });

        return (
            <Table size="small" sx={{ mt: 1 }}>
                <TableHead sx={{ backgroundColor: '#F1F4F9' }}>
                    <TableRow>
                        <TableCell>Field Name</TableCell>
                        <TableCell align="center">Old Value</TableCell>
                        <TableCell align="center">Proposed Value</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {diffs.map(diff => (
                        <TableRow key={diff.field}>
                            <TableCell sx={{ fontWeight: 600 }}>{diff.field.replace(/_/g, ' ').toUpperCase()}</TableCell>
                            <TableCell align="center" sx={{ color: 'error.main', fontWeight: 500 }}>
                                {diff.field === 'location' ? (diff.oldVal === '1' ? 'Chennai' : 'Trichy') : diff.oldVal}
                            </TableCell>
                            <TableCell align="center" sx={{ color: 'success.main', fontWeight: 600 }}>
                                {diff.field === 'location' ? (diff.newVal === '1' ? 'Chennai' : 'Trichy') : diff.newVal}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    };

    return (
        <Box sx={{ p: 4, backgroundColor: '#F1F4F9', minHeight: '100vh' }}>
            <ToastContainer position="top-center" autoClose={2000} theme="colored" />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ color: '#673ab7', fontWeight: 600 }}>
                    Pending Edit Requests
                </Typography>
            </Box>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                    <CircularProgress size={50} />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {!loading && requests.length === 0 && (
                <Alert severity="info">
                    No pending edit requests found.
                </Alert>
            )}

            {!loading && requests.length > 0 && (
                <Paper sx={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ backgroundColor: '#DCE3E7' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Requestor Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Student/Batch Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>ID/User ID</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Batch</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Course</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {requests.map(req => {
                                    const isBatch = req.module === 'batch_assessments' || req.module === 'batch_profile';
                                    const isCertificate = req.module === 'certificate' || req.module === 'certificate_status' || req.module === 'certificate_delete';
                                    const isCourse = req.module === 'course';

                                    let studentBatchName, idDisplay, batchDisplay, courseDisplay;
                                    if (isCourse) {
                                        studentBatchName = req.course?.course_name || req.old_data?.course_name || 'N/A';
                                        idDisplay = `Course ID: ${req.reference_id ?? 'N/A'}`;
                                        batchDisplay = '—';
                                        courseDisplay = req.course?.course_name || req.old_data?.course_name || 'N/A';
                                    } else if (isBatch) {
                                        studentBatchName = req.batch?.batch_name || 'N/A';
                                        idDisplay = `Batch ID: ${req.batch?.id}`;
                                        batchDisplay = req.batch?.batch_name || 'N/A';
                                        courseDisplay = req.batch?.course?.course_name || 'N/A';
                                    } else if (isCertificate) {
                                        studentBatchName = req.certificate?.student_name || 'N/A';
                                        idDisplay = req.certificate?.user_id || 'N/A';
                                        batchDisplay = req.certificate?.batch || 'N/A';
                                        courseDisplay = req.certificate?.course || 'N/A';
                                    } else {
                                        studentBatchName = req.student?.name || 'N/A';
                                        idDisplay = req.student?.userid || 'N/A';
                                        batchDisplay = req.student?.batch?.batch_name || 'N/A';
                                        courseDisplay = req.student?.course?.course_name || 'N/A';
                                    }

                                    const moduleChipSx = isCourse
                                        ? { backgroundColor: '#fff3e0', color: '#e65100' }
                                        : isBatch
                                        ? { backgroundColor: '#b2dfdb', color: '#004d40' }
                                        : { backgroundColor: '#e1bee7', color: '#4a148c' };

                                    return (
                                        <TableRow key={req.id} hover>
                                            <TableCell>{req.requestor?.name || 'N/A'}</TableCell>
                                            <TableCell>{studentBatchName}</TableCell>
                                            <TableCell>{idDisplay}</TableCell>
                                            <TableCell>{batchDisplay}</TableCell>
                                            <TableCell>{courseDisplay}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={req.location?.name || 'All'}
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={req.module.toUpperCase()}
                                                    size="small"
                                                    sx={moduleChipSx}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<VisibilityIcon />}
                                                        onClick={() => handleOpenDetails(req)}
                                                    >
                                                        View
                                                    </Button>
                                                    <IconButton
                                                        color="success"
                                                        size="small"
                                                        onClick={() => handleOpenDetails(req)}
                                                        disabled={actionLoading}
                                                    >
                                                        <CheckIcon />
                                                    </IconButton>
                                                    <IconButton
                                                        color="error"
                                                        size="small"
                                                        onClick={() => handleOpenDetails(req)}
                                                        disabled={actionLoading}
                                                    >
                                                        <CloseIcon />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            )}

            {/* Request Details Dialog */}
            <Dialog open={openModal} onClose={handleCloseDetails} maxWidth="md" fullWidth>
                {selectedRequest && (
                    <>
                        <DialogTitle sx={{ backgroundColor: '#673ab7', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">Edit Request Details</Typography>
                            <IconButton onClick={handleCloseDetails} sx={{ color: 'white' }}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent sx={{ p: 3 }}>
                            <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">Requestor</Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{selectedRequest.requestor?.name || 'N/A'}</Typography>
                                </Grid>
                                {selectedRequest.module === 'course' ? (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">Course Name</Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {selectedRequest.course?.course_name || selectedRequest.old_data?.course_name || 'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">Course ID</Typography>
                                            <Typography variant="subtitle1">
                                                {selectedRequest.reference_id ?? 'N/A'}
                                            </Typography>
                                        </Grid>
                                    </>
                                ) : selectedRequest.module === 'certificate' ? (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">Student Name (User ID)</Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {selectedRequest.certificate?.student_name || 'N/A'} ({selectedRequest.certificate?.user_id || 'N/A'})
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">Batch & Course</Typography>
                                            <Typography variant="subtitle1">
                                                {selectedRequest.certificate?.batch || 'N/A'} - {selectedRequest.certificate?.course || 'N/A'}
                                            </Typography>
                                        </Grid>
                                    </>
                                ) : selectedRequest.module !== 'batch_assessments' && selectedRequest.module !== 'batch_profile' ? (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">Student Name (User ID)</Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {selectedRequest.student?.name || 'N/A'} ({selectedRequest.student?.userid || 'N/A'})
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">Batch & Course</Typography>
                                            <Typography variant="subtitle1">
                                                {selectedRequest.student?.batch?.batch_name || 'N/A'} - {selectedRequest.student?.course?.course_name || 'N/A'}
                                            </Typography>
                                        </Grid>
                                    </>
                                ) : (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">Batch Name (ID)</Typography>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {selectedRequest.batch?.batch_name || 'N/A'} ({selectedRequest.batch?.id || 'N/A'})
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Typography variant="body2" color="textSecondary">Course Name</Typography>
                                            <Typography variant="subtitle1">
                                                {selectedRequest.batch?.course?.course_name || 'N/A'}
                                            </Typography>
                                        </Grid>
                                    </>
                                )}
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">Date</Typography>
                                    <Typography variant="subtitle1">
                                        {new Date(selectedRequest.created_at).toLocaleDateString()}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" sx={{ mb: 2, color: '#333' }}>Proposed Changes</Typography>
                            {selectedRequest.module === 'marks' && renderMarksComparison(selectedRequest)}
                            {selectedRequest.module === 'batch_assessments' && renderBatchAssessmentsComparison(selectedRequest)}
                            {selectedRequest.module === 'student_profile' && renderStudentProfileComparison(selectedRequest)}
                            {selectedRequest.module === 'batch_profile' && renderBatchProfileComparison(selectedRequest)}
                            {(selectedRequest.module === 'certificate' || selectedRequest.module === 'certificate_status') && renderCertificateComparison(selectedRequest)}
                            {selectedRequest.module === 'certificate_delete' && renderCertificateDeleteComparison(selectedRequest)}
                            {selectedRequest.module === 'course' && renderCourseComparison(selectedRequest)}
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" sx={{ mb: 1, color: '#333' }}>Remarks (optional)</Typography>
                            <TextField
                                multiline
                                minRows={2}
                                fullWidth
                                placeholder="Add a note for the requester about this decision..."
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                disabled={actionLoading}
                            />
                        </DialogContent>
                        <DialogActions sx={{ p: 2, justifyContent: 'flex-end', gap: 2 }}>
                            <Button variant="outlined" onClick={handleCloseDetails} disabled={actionLoading}>
                                Close
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<CloseIcon />}
                                onClick={() => handleReject(selectedRequest.id)}
                                disabled={actionLoading}
                            >
                                Reject Changes
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<CheckIcon />}
                                onClick={() => handleApprove(selectedRequest.id)}
                                disabled={actionLoading}
                            >
                                Approve & Apply
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
}
